import type { DefaultSommelierDto, SearchSommeliersDto, SlugCheckResultDto, SommelierPublicDto } from './models';
import { RestService, Rest } from '@abp/ng.core';
import type { PagedResultDto } from '@abp/ng.core';
import { Injectable } from '@angular/core';
import type { CountryDto } from '../countries/models';
import type { WineryDto } from '../wineries/models';
import type { GetWineDto, WineDto } from '../wines/models';
import type { RestockSubscriptionResultDto, SubscribeRestockDto } from '../wines/restock-subscription-models';

@Injectable({
  providedIn: 'root',
})
export class CatalogService {
  apiName = 'Default';
  

  checkSlug = (slug: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, SlugCheckResultDto>({
      method: 'POST',
      url: '/api/app/catalog/check-slug',
      params: { slug },
    },
    { apiName: this.apiName,...config });
  

  getBySlug = (slug: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, SommelierPublicDto>({
      method: 'GET',
      url: '/api/app/catalog/by-slug',
      params: { slug },
    },
    { apiName: this.apiName,...config });
  

  getDefaultSommelier = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, DefaultSommelierDto>({
      method: 'GET',
      url: '/api/app/catalog/default-sommelier',
    },
    { apiName: this.apiName,...config });
  

  getCountries = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, CountryDto[]>({
      method: 'GET',
      url: '/api/app/catalog/countries',
    },
    { apiName: this.apiName,...config });
  

  getFileByFileAttachmentId = (fileAttachmentId: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, Blob>({
      method: 'GET',
      responseType: 'blob',
      url: `/api/app/catalog/file/${fileAttachmentId}`,
    },
    { apiName: this.apiName,...config });
  

  getWine = (wineId: number, sommelierSlug?: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, WineDto>({
      method: 'GET',
      url: `/api/app/catalog/wine/${wineId}`,
      params: { sommelierSlug },
    },
    { apiName: this.apiName,...config });
  

  searchSommeliers = (input: SearchSommeliersDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<SommelierPublicDto>>({
      method: 'GET',
      url: '/api/app/catalog/search-sommeliers',
      params: { province: input.province, locality: input.locality, keyword: input.keyword, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });


  getWineImageUrlByFileIdAndMaxWidthAndMaxHeight = (fileId: number, maxWidth: number, maxHeight: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, string>({
      method: 'GET',
      responseType: 'text',
      url: `/api/app/catalog/wine-image-url/${fileId}`,
      params: { maxWidth, maxHeight },
    },
    { apiName: this.apiName,...config });
  

  getWineriesByCountryAndRegion = (country: string, region: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, WineryDto[]>({
      method: 'GET',
      url: '/api/app/catalog/wineries',
      params: { country, region },
    },
    { apiName: this.apiName,...config });


  getLandingWineries = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, WineryDto[]>({
      method: 'GET',
      url: '/api/app/catalog/landing-wineries',
    },
    { apiName: this.apiName,...config });


  getWineSuggestions = (input: GetWineDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, WineDto[]>({
      method: 'GET',
      url: '/api/app/catalog/wine-suggestions',
      params: { keyword: input.keyword, country: input.country, sommelierSlug: input.sommelierSlug, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });


  getWinesPaged = (input: GetWineDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<WineDto>>({
      method: 'GET',
      url: '/api/app/catalog/wines-paged',
      params: { keyword: input.keyword, country: input.country, wineryId: input.wineryId, region: input.region, sommelierSlug: input.sommelierSlug, externalCode: input.externalCode, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });

  // winepassport#364 (WP-018) — "Avisame cuando vuelva".
  subscribeToRestock = (wineId: number, input: SubscribeRestockDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, RestockSubscriptionResultDto>({
      method: 'POST',
      url: `/api/app/catalog/wine/${wineId}/restock-subscription`,
      body: input,
    },
    { apiName: this.apiName,...config });

  constructor(private restService: RestService) {}
}
